import React, { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createId } from "@/utils/id";
import { ConfirmDialog, type ConfirmDialogProps } from "./confirm";

type DialogContextType = {
  confirm: (options: ConfirmDialogProps) => Promise<any>;
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dialogs, setDialogs] = useState<ReactNode[]>([]);
  const resolverStack = useRef<((value: any) => void)[]>([]);

  const showDialog = useCallback((node: ReactNode) => {
    return new Promise((resolve) => {
      setDialogs((prev) => [...prev, node]);
      resolverStack.current.push(resolve);
    });
  }, []);

  const closeDialog = (result: any) => {
    setDialogs((prev) => prev.slice(0, -1)); // 移除最顶层
    const resolve = resolverStack.current.pop();
    resolve?.(result);
  };

  const confirm = (options: ConfirmDialogProps): Promise<any> => {
    return showDialog(
      <ConfirmDialog
        {...options}
        key={Math.random()} // 确保每次都是唯一组件
        open={true}
        onOpenChange={(open) => {
          if (!open) closeDialog(false);
        }}
      />,
    );
  };

  return (
    <DialogContext.Provider value={{ confirm }}>
      {children}
      {createPortal(
        dialogs.map((dialog) => <React.Fragment key={createId()}>{dialog}</React.Fragment>),
        document.body,
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextType => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used within a DialogProvider");
  return ctx;
};
